import { registerAuditLabels, type StatusMap } from "@vixio/ui";
import { downloadFileLink, rpc, type FileLink } from "@vixio/data";
import type { CertificateData, CertificateLayout } from "@vixio/certificates/layout";

/** Tipos das linhas e RPCs do módulo certificates (ver modules/certificates/README.md). */

export const CERT_STATUS: StatusMap = {
  valid: { label: "válido", tone: "ok" },
  revoked: { label: "revogado", tone: "crit" },
};

export const ORIGIN_LABEL: Record<string, string> = {
  "programs.enrollment": "Conclusão de turma",
  "talks.talk": "Palestra",
  manual: "Emissão avulsa",
};

export const KIND_LABEL: Record<string, string> = { programa: "Programa", palestra: "Palestra", evento: "Evento", geral: "Geral" };
export const DEFAULT_FOR_LABEL: Record<string, string> = { programs: "Padrão das turmas", talks: "Padrão das palestras" };

export interface CertificateView {
  id: string;
  org_id: string;
  person_id: string;
  recipient_name: string;
  person_email: string | null;
  title: string;
  hours: number;
  origin_type: string;
  origin_id: string | null;
  batch_id: string | null;
  template_id: string;
  template_name: string | null;
  template_version: number | null;
  code: string;
  document_id: string;
  content_sha256: string;
  status: "valid" | "revoked";
  issued_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  class_title: string | null;
  completed_on: string | null;
}

export interface TemplateView {
  id: string;
  org_id: string;
  key: string;
  name: string;
  kind: string;
  default_for: "programs" | "talks" | null;
  active: boolean;
  current_version_id: string | null;
  current_version: number | null;
  layout: CertificateLayout | null;
  version_created_at: string | null;
  issued_count: number;
}

export interface RenderPayload {
  certificate_id: string;
  code: string;
  status: "valid" | "revoked";
  issued_at: string;
  revoked_at: string | null;
  title: string;
  recipient_name: string;
  hours: number;
  data: CertificateData;
  content_sha256: string;
  verify_url: string;
  layout: CertificateLayout | null;
  template_version: number | null;
}

/** Gera o PDF no navegador e baixa. */
export async function downloadCertificate(p: RenderPayload): Promise<void> {
  // pdf-lib só carrega quando alguém baixa um certificado (fora do pacote inicial do painel).
  const { renderCertificatePdf } = await import("@vixio/certificates/render");
  const r = await renderCertificatePdf({
    layout: p.layout, data: p.data, code: p.code, verifyUrl: p.verify_url, contentSha256: p.content_sha256, status: p.status,
    now: new Date(p.issued_at),
  });
  const blob = new Blob([r.bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `certificado-${p.code}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Baixa o PDF guardado no servidor (certificates.pdf_link: equipe ou a própria pessoa; URL assinada do Storage).
 * Sem PDF guardado ainda (ou sem login, na consulta pública), gera no navegador com o mesmo renderizador e a mesma
 * data de emissão: os bytes e o hash são os mesmos do arquivo guardado.
 */
export async function downloadCertificatePdf(p: RenderPayload, o: { stored: boolean }): Promise<"stored" | "browser"> {
  if (o.stored) {
    try {
      await downloadFileLink(await rpc("certificates", "pdf_link", { p_certificate: p.certificate_id }));
      return "stored";
    } catch {
      /* ainda não guardado: gera aqui */
    }
  }
  await downloadCertificate(p);
  return "browser";
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "certificates.install": { args: { p_org: string }; returns: null };
    "certificates.validate_layout": { args: { p_layout: CertificateLayout }; returns: { valid: boolean; problems: string[] } };
    "certificates.create_template": {
      args: { p_org: string; p_key: string; p_name: string; p_kind?: string; p_layout?: CertificateLayout | null; p_default_for?: string | null };
      returns: string;
    };
    "certificates.save_template_version": {
      args: { p_template: string; p_layout: CertificateLayout; p_note?: string | null };
      returns: { version_id: string; created: boolean; version: number };
    };
    "certificates.update_template": {
      args: { p_template: string; p_name: string; p_kind: string; p_default_for?: string | null; p_active?: boolean };
      returns: null;
    };
    "certificates.issue": {
      args: { p_org: string; p_person: string; p_title: string; p_hours: number; p_data?: Record<string, unknown>; p_template?: string | null; p_origin_type?: string; p_origin_id?: string | null };
      returns: { certificate_id: string; code: string; created: boolean };
    };
    "certificates.issue_batch": {
      args: { p_org: string; p_title: string; p_hours: number; p_people: string[]; p_data?: Record<string, unknown>; p_template?: string | null; p_origin_type?: string; p_origin_id?: string | null };
      returns: { batch_id: string; issued: number; skipped: number; codes: string[] };
    };
    "certificates.revoke": { args: { p_certificate: string; p_reason: string }; returns: null };
    "certificates.render_data": { args: { p_certificate: string }; returns: RenderPayload };
    "certificates.pdf_link": { args: { p_certificate: string }; returns: FileLink };
    "certificates.request_lookup_code": { args: { p_org_slug: string; p_email: string }; returns: { ok: boolean; message?: string; error?: string } };
    "certificates.lookup_with_code": {
      args: { p_org_slug: string; p_email: string; p_code: string };
      returns: { ok: boolean; error?: string; certificates?: RenderPayload[] };
    };
  }
}

registerAuditLabels({
  "certificates.template_created": "Modelo de certificado criado",
  "certificates.template_version_saved": "Nova versão do layout",
  "certificates.template_updated": "Modelo de certificado alterado",
  "certificates.certificate_issued": "Certificado emitido",
  "certificates.certificate_revoked": "Certificado revogado",
  "certificates.batch_issued": "Lote de certificados emitido",
  "certificates.lookup_requested": "Consulta pública pedida",
  "certificates.lookup_confirmed": "Consulta pública confirmada",
});
